import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Button,
  GridTable,
  SecondaryHeader,
  SecondaryHeaderButtonWrap,
  SecondaryHeaderInfo,
  SecondaryHeaderSearchField,
  Spinner,
} from "olive-react-myoneui";
import { ToastContainer, toast } from "react-toastify";
import AddEditOffcanvas from "./AddEditOffcanvas";
import { getSubTopic } from "../../../api/subTopicApis";
import {
  createChapter,
  deleteChapter,
  getChapters,
  updateChapter,
} from "../../../api/chapterApi";
import DataNotFound from "../component/DataNotFound";
import PaginationTemplate from "../component/grid-table/PaginationTemplate";
import TextTemplate from "../component/grid-table/TextTemplate";
import { penEditIcon, removeIcon } from "../../../constants/icons";
import { formatDate, debounce } from "../../../utils/helper";
import ConfirmBox from "../component/ConfirmBox";

const Chapter = () => {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [title, setTitle] = useState("");
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [subTopics, setSubtopics] = useState([]);
  const [subtopicPage, setSubtopicPage] = useState(1);
  const [searchSubtopic, setSearchSubtopic] = useState("");
  const [subTopicLoading, setSubTopicLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [chapterCreateUpdateLoading, setChapterCreateUpdateLoading] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [chapterLimit, setChapterLimit] = useState(20);
  const [totalChapters, setTotalChapters] = useState(0);
  const [totalChapterPage, setTotalChapterPage] = useState(0);
  const [currentChapterPage, setCurrentChapterPage] = useState(1);
  const [type, setType] = useState("add");
  const [errors, setErrors] = useState({});
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [deleteChapterLoading, setDeleteChapterLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const updateSearchWithDebounce = useCallback(
    debounce((val) => {
      setDebouncedSearch(val);
      setCurrentChapterPage(1);
    }, 500),
    [],
  );

  const onSearchChange = (e) => {
    setSearch(e.target.value);
    updateSearchWithDebounce(e.target.value);
  };

  const onClearSearch = () => {
    setSearch("");
    updateSearchWithDebounce("");
  };

  const tableManager = useRef(null);
  const setTableManager = (tm) => (tableManager.current = tm);

  const handleClose = () => {
    handleClear();
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    if (name === "title") {
      setTitle(value);
      if (errors.title) setErrors((prev) => ({ ...prev, title: null }));
    }
  };

  const getSubtopics = async (page = 1, searchParam = searchSubtopic) => {
    setSubTopicLoading(true);
    if (page === 1) setSubtopics([]);
    try {
      const response = await getSubTopic({
        page,
        limit: 20,
        title:
          searchParam && searchParam?.trim()
            ? encodeURIComponent(searchParam.trim())
            : "",
      });

      if (response?.data?.success) {
        const newData = response.data?.data || [];
        const metadata = response.data?.pagination || {};

        const totalPages = metadata?.totalPages || 1;
        const currentPage = metadata?.currentPage || page;

        if (currentPage === 1) {
          setSubtopics(newData);
          setSubtopicPage(currentPage);
        } else {
          setSubtopics((prev) => [...prev, ...newData]);
          setSubtopicPage(currentPage);
        }

        setHasMore(currentPage < totalPages);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubTopicLoading(false);
    }
  };

  const handleOffCanvas = () => {
    setShowOffcanvas(true);
    getSubtopics(1);
  };

  const handleLoadMore = () => {
    if (subTopicLoading || !hasMore) return;
    setSubtopicPage((prev) => {
      const nextPage = prev + 1;
      getSubtopics(nextPage);
      return nextPage;
    });
  };

  const handleClear = () => {
    setTitle("");
    setSelectedSubtopic(null);
    setSubtopics([]);
    setSubtopicPage(1);
    setShowOffcanvas(false);
    setType("add");
    setSelectedChapterId("");
    setShowConfirmBox(false);
    setErrors({});
  };

  const handleSubmit = () => {
    let newErrors = {};
    if (!title?.trim()) {
      newErrors.title = "Chapter title is required";
    }
    if (!selectedSubtopic) {
      newErrors.subtopic = "Sub Topic is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const payload = {
      title,
      subTopic: selectedSubtopic?._id,
    };

    setChapterCreateUpdateLoading(true);
    if (type === "add") {
      createChapters(payload);
    } else if (type === "edit") {
      editChapters(payload);
    }
  };

  const createChapters = (payload) => {
    createChapter(payload)
      .then((res) => {
        if (res?.success) {
          getPaginatedChapters();
          toast.success("Chapter added successfully.");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
        console.log(error);
      })
      .finally(() => {
        setChapterCreateUpdateLoading(false);
        handleClear();
      });
  };

  const editChapters = (payload) => {
    updateChapter(selectedChapterId, payload)
      .then((res) => {
        if (res?.success) {
          getPaginatedChapters();
          toast.success("Chapter updated successfully.");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
        console.log(error);
      })
      .finally(() => {
        setChapterCreateUpdateLoading(false);
        handleClear();
      });
  };

  const getPaginatedChapters = (currentPage, limit, searchText) => {
    const payload = {
      page: currentPage || currentChapterPage,
      limit: limit || chapterLimit,
      search: searchText !== undefined ? searchText : debouncedSearch,
    };
    setLoading(true);
    getChapters(payload)
      .then((response) => {
        if (response?.success) {
          setChapters(response?.data || []);
          setChapterLimit(Number(response?.metadata?.perPage));
          setTotalChapterPage(Number(response?.metadata?.totalPages));
          setTotalChapters(Number(response?.metadata?.totalItems));
          setCurrentChapterPage(Number(response?.metadata?.page));
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columnData = [
    {
      id: 0,
      visible: true,
      label: "S No.",
      width: "1fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ rowIndex }) => <TextTemplate value={rowIndex} />,
    },

    {
      id: 1,
      field: "title",
      visible: true,
      label: "Chapter",
      width: "3fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: TextTemplate,
    },
    {
      id: 2,
      visible: true,
      label: "Sub Topic",
      width: "3fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ data }) => (
        <TextTemplate value={data.subTopic?.title || "N/A"} />
      ),
    },
    {
      id: 3,
      visible: true,
      label: "Created At",
      width: "2fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ data }) => (
        <TextTemplate value={formatDate(data?.createdAt) || "N/A"} />
      ),
    },
    {
      id: 4,
      label: `Action`,
      width: "3fr",
      sortable: false,
      resizable: false,
      className: "grid-header-text-center",
      cellRenderer: ({ data }) => (
        <div className="rgt-cell-inner rgt-text-truncate program-edit-hover justify-content-center">
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleEdit(data)}
            disabled={loading}
          >
            {penEditIcon} Edit
          </Button>
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleDelete(data?._id)}
            disabled={loading}
          >
            {removeIcon} Delete
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = (data) => {
    if (!data?._id) return;
    setType("edit");
    setShowOffcanvas(true);
    setTitle(data?.title);
    setSelectedSubtopic(data?.subTopic);
    setSelectedChapterId(data?._id);
    getSubtopics(1);
  };

  const deleteSelectedChapter = () => {
    setDeleteChapterLoading(true);
    deleteChapter(selectedChapterId)
      .then((res) => {
        if (res?.success) {
          handleClear();
          getPaginatedChapters();
          toast.success("Chapter deleted successfully.");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Error deleting chapter");
      })
      .finally(() => {
        setDeleteChapterLoading(false);
      });
  };

  const handleHideConfirmBox = () => {
    setShowConfirmBox(false);
    setSelectedChapterId("");
  };

  const handleDelete = (id) => {
    if (!id) return;
    setSelectedChapterId(id);
    setShowConfirmBox(id);
  };

  const handlePageSizeChange = (size) => {
    if (!size || Number(size) === Number(chapterLimit)) return;
    setChapterLimit(Number(size));
    setCurrentChapterPage(1);
    // getPaginatedChapters();
  };

  const updateSubtopicSearchWithDebounce = useCallback(
    debounce((val) => {
      setSubtopicPage(1);
      getSubtopics(1, val);
    }, 500),
    [],
  );

  const handleSearchSubtopicChange = (val) => {
    setSearchSubtopic(val);
    updateSubtopicSearchWithDebounce(val);
  };

  useEffect(() => {
    getPaginatedChapters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterLimit, currentChapterPage, debouncedSearch]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="content-wrapper">
        <SecondaryHeader>
          <SecondaryHeaderInfo
            SecondaryHeaderTitle="Chapters"
            SecondaryHeaderText={
              totalChapters === 0
                ? "No Chapters"
                : `${totalChapters} ${totalChapters === 1 ? "Chapter" : "Chapters"}`
            }
            SecondaryText={true}
          />
          <SecondaryHeaderSearchField
            placeholder="Search Chapters..."
            value={search}
            onChange={onSearchChange}
            onClear={onClearSearch}
            disabled={loading || deleteChapterLoading}
          />
          <SecondaryHeaderButtonWrap>
            <Button
              variant="secondary"
              onClick={handleOffCanvas}
              disabled={loading}
            >
              Add
            </Button>
          </SecondaryHeaderButtonWrap>
        </SecondaryHeader>
        <div className="content-block p-2" style={{ position: "relative" }}>
          {loading ? (
            <div className="spinner-wrapper">
              <Spinner animation="border" />
            </div>
          ) : Array.isArray(chapters) && chapters.length > 0 ? (
            <GridTable
              key={chapters.length}
              searchText=""
              highlightSearch={false}
              enableColumnsReorder={false}
              columns={columnData}
              onRowsRequest={() => ({
                rows: chapters,
                totalRows: totalChapters,
              })}
              page={currentChapterPage}
              pageSize={chapterLimit}
              setPage={setCurrentChapterPage}
              setPageSize={handlePageSizeChange}
              totalPages={totalChapterPage}
              // filter={filter}
              onLoad={setTableManager}
              components={{
                Footer: (props) => (
                  <PaginationTemplate
                    {...props}
                    page={currentChapterPage}
                    pageSize={chapterLimit}
                    totalPages={totalChapterPage}
                    onPageChange={setCurrentChapterPage}
                    onPageSizeChange={handlePageSizeChange}
                    disabled={loading}
                  />
                ),
              }}
            />
          ) : (
            <DataNotFound title="No chapters found" nochange={true} />
          )}
        </div>
      </div>

      <ConfirmBox
        show={showConfirmBox}
        question="Are you sure you want to delete Chapter?"
        okText="Delete"
        cancelText="Cancel"
        onHide={handleHideConfirmBox}
        onSubmit={deleteSelectedChapter}
        buttonLoading={deleteChapterLoading}
      />

      <AddEditOffcanvas
        show={showOffcanvas}
        onClose={handleClose}
        type={type}
        handleChange={handleChange}
        title={title}
        selectedSubtopic={selectedSubtopic}
        subTopics={subTopics}
        setSelectedSubtopic={(val) => {
          setSelectedSubtopic(val);
          if (errors.subtopic)
            setErrors((prev) => ({ ...prev, subtopic: null }));
        }}
        searchValue={searchSubtopic}
        setSearchValue={handleSearchSubtopicChange}
        hasMore={hasMore}
        handleLoadMore={handleLoadMore}
        isLoading={subTopicLoading}
        onSubmit={handleSubmit}
        onButtonLoading={chapterCreateUpdateLoading}
        errors={errors}
      />
    </>
  );
};

export default Chapter;
